// apps/web/src/features/commercial-proposals/components/CommercialProposalDrawerBody.tsx
//
// @aiPurpose read-only detail view inside the proposal drawer (#1198):
//   status badge, owner, timestamps, the lifetime HTML view count
//   (separate from PDF downloads), the backend `calculation`, and an
//   optional internal preview. Action buttons live in
//   `CommercialProposalDrawerActions`.
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CommercialProposalPublicViewKind,
  CommercialProposalStatus,
  type CommercialProposalDetailResponse,
} from '@magimp/shared';

import { StatusBadge } from '../../../ui/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { formatDateTime } from '../../../i18n/index.js';
import type { AppLocale } from '../../../i18n/index.js';
import { useCommercialProposalViews } from '../queries.js';
import { CommercialProposalCalcPanel } from './CommercialProposalCalcPanel.js';
import { CommercialProposalCalcParams } from './CommercialProposalCalcParams.js';
import { CommercialProposalPreview } from './CommercialProposalPreview.js';
import { SavedProductPhotoField } from './SavedProductPhoto.js';

function statusBadgeStatus(status: CommercialProposalStatus): 'draft' | 'active' {
  return status === CommercialProposalStatus.PUBLISHED ? 'active' : 'draft';
}

export function CommercialProposalDrawerBody({
  detail,
  language,
  previewOpen,
}: {
  detail: CommercialProposalDetailResponse;
  language: AppLocale;
  previewOpen: boolean;
}) {
  const { t } = useTranslation('commercialProposals');
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const { proposal, calculation } = detail;

  // The lifetime view analytics are a separate read; HTML views and PDF
  // downloads are split here so the two counts never blur together.
  const viewsQ = useCommercialProposalViews(proposal.id);
  const { htmlViews, pdfDownloads } = useMemo(() => {
    const items = viewsQ.data?.items ?? [];
    return {
      htmlViews: items.filter((v) => v.kind === CommercialProposalPublicViewKind.HTML_VIEW).length,
      pdfDownloads: items.filter((v) => v.kind === CommercialProposalPublicViewKind.PDF_DOWNLOAD)
        .length,
    };
  }, [viewsQ.data?.items]);

  const isPublished = proposal.status === CommercialProposalStatus.PUBLISHED;
  const ownerLabel = proposal.ownerId === currentUserId ? t('drawer.ownerYou') : proposal.ownerId;

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 4 }}>
          {t('drawer.fields.status')}
        </div>
        <StatusBadge status={statusBadgeStatus(proposal.status)} />
      </div>

      {isPublished && (
        <p
          data-testid="commercial-proposals-published-lock"
          style={{ fontSize: 12, color: 'var(--fg-3)', margin: '0 0 14px' }}
        >
          {t('editor.publishedLocked')}
        </p>
      )}

      <Field label={t('drawer.fields.client')} value={proposal.content.client || '—'} />
      <Field label={t('drawer.fields.productName')} value={proposal.content.productName} />
      {proposal.content.photoObjectKey && <SavedProductPhotoField proposalId={proposal.id} />}
      <Field label={t('drawer.fields.owner')} value={ownerLabel} />
      <Field
        label={t('drawer.fields.updatedAt')}
        value={formatDateTime(proposal.updatedAt, language)}
      />
      {proposal.publishedAt && (
        <Field
          label={t('drawer.fields.publishedAt')}
          value={formatDateTime(proposal.publishedAt, language)}
        />
      )}
      {isPublished && (
        <>
          <Field
            label={t('drawer.fields.htmlViews')}
            value={String(htmlViews)}
            testid="commercial-proposals-html-views"
          />
          <Field label={t('drawer.fields.pdfDownloads')} value={String(pdfDownloads)} />
        </>
      )}

      <CommercialProposalCalcPanel backend={calculation} language={language} />
      {isPublished && (
        <CommercialProposalCalcParams content={proposal.content} language={language} />
      )}

      {previewOpen && (
        <div style={{ marginTop: 16 }}>
          <CommercialProposalPreview
            content={proposal.content}
            calculation={calculation}
            proposalId={proposal.id}
          />
        </div>
      )}
    </>
  );
}

function Field({ label, value, testid }: { label: string; value: string; testid?: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 2 }}>{label}</div>
      <div data-testid={testid} style={{ fontSize: 14, color: 'var(--fg-1)' }}>
        {value}
      </div>
    </div>
  );
}
